import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { getPblLawyerCvRevisionForReview, reviewPblLawyerCvRevision } from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import CvAuthorisationDownload from '@/components/Pbl/CvAuthorisationDownload';
import { authorisationConfirmations } from '../LawyerCv/AuthorisationFields';
import {
  inferPblLanguageOtherText,
  inferPblProfessionalQualificationOtherText,
  normalizePblLanguageValues,
  normalizePblLawFirmPositionValue,
  normalizePblProfessionalQualificationValues,
  normalizePblServiceAreaValues,
} from '@/components/Pbl/VolunteerRegistration/profileCvValues';
import { Button } from '@/components/UI/Button';
import { LoadingPage } from '@/layout';
import PATH from '@/router/path';
import type { PblCvAuthorisation, PblLawyerCvRevision, PblLawyerProfile } from '@/types/pbl';
import { formatPblDisplayDate } from '@/utils/pblDate';
import { translatePblDisplayList, translatePblDisplayValue } from '@/utils/pblDisplayTranslation';
import { errorHandler, successHandler } from '@/utils/toastHandler';

import {
  ActionRow,
  ChangedBadge,
  ComparisonHeader,
  DecisionSection,
  EmptyState,
  FieldError,
  FieldLabel,
  FieldRow,
  LawyerName,
  MobileVersionLabel,
  ReasonHelp,
  ReasonLabel,
  ReasonTextarea,
  ReviewIntro,
  ReviewPanel,
  ReviewSection,
  RevisionMeta,
  RevisionSummary,
  SectionTitle,
  VersionValue,
} from './styles';

type ReviewDecision = 'approve' | 'return' | 'reject';
type FieldFormat = 'boolean' | 'list' | 'raw' | 'value';
type ReviewTranslator = (key: string, options?: Record<string, unknown>) => string;

type ReviewFieldDefinition = {
  defaultLabel: string;
  format?: FieldFormat;
  key: string;
  labelKey: string;
};

type ReviewSectionDefinition = {
  defaultTitle: string;
  fields: ReviewFieldDefinition[];
  key: string;
  titleKey: string;
};

const reviewSections: ReviewSectionDefinition[] = [
  {
    key: 'personalInformation',
    titleKey: 'pbl.lawyerRegistration.reviewSubmit.sections.personalInformation',
    defaultTitle: 'Personal Information',
    fields: [
      {
        key: 'full_name_en',
        labelKey: 'pbl.lawyerRegistration.reviewSubmit.labels.fullNameEn',
        defaultLabel: 'Full name (English)',
      },
      {
        key: 'full_name_zh',
        labelKey: 'pbl.lawyerRegistration.reviewSubmit.labels.fullNameZh',
        defaultLabel: 'Full name (Chinese)',
      },
      {
        key: 'hk_solicitor_admission_year',
        labelKey: 'pbl.lawyerRegistration.reviewSubmit.labels.admissionYear',
        defaultLabel: 'Year of admission as a Hong Kong solicitor',
      },
    ],
  },
  {
    key: 'practiceInformation',
    titleKey: 'pbl.lawyerRegistration.reviewSubmit.sections.practiceInformation',
    defaultTitle: 'Practice Information',
    fields: [
      {
        key: 'law_firm_name',
        labelKey: 'pbl.lawyerRegistration.reviewSubmit.labels.lawFirmName',
        defaultLabel: 'Law firm name (English)',
      },
      {
        key: 'law_firm_name_zh',
        labelKey: 'pbl.lawyerRegistration.reviewSubmit.labels.lawFirmNameZh',
        defaultLabel: 'Law firm name (Chinese)',
      },
      {
        key: 'position_in_law_firm',
        labelKey: 'pbl.lawyerRegistration.reviewSubmit.labels.position',
        defaultLabel: 'Position in law firm',
        format: 'value',
      },
      {
        key: 'position_other_text',
        labelKey: 'pbl.lawyerRegistration.practiceInformation.fields.otherPosition',
        defaultLabel: 'Other position',
      },
      {
        key: 'other_professional_qualifications',
        labelKey: 'pbl.lawyerRegistration.reviewSubmit.labels.otherProfessionalQualifications',
        defaultLabel: 'Other professional qualifications',
        format: 'list',
      },
      {
        key: 'other_professional_qualification_text',
        labelKey: 'pbl.lawyerRegistration.personalInformation.fields.pleaseSpecify',
        defaultLabel: 'Other professional qualification details',
      },
      {
        key: 'language_proficiency',
        labelKey: 'pbl.lawyerRegistration.reviewSubmit.labels.languageProficiency',
        defaultLabel: 'Language proficiency',
        format: 'list',
      },
      {
        key: 'language_other_text',
        labelKey: 'pbl.lawyerRegistration.personalInformation.fields.otherLanguage',
        defaultLabel: 'Other language',
      },
      {
        key: 'service_areas',
        labelKey: 'pbl.lawyerRegistration.reviewSubmit.labels.selectApplicableAreas',
        defaultLabel: 'Pro bono legal service areas',
        format: 'list',
      },
    ],
  },
  {
    key: 'profileCv',
    titleKey: 'pbl.lawyerRegistration.reviewSubmit.sections.profileCv',
    defaultTitle: 'Profile & CV',
    fields: [
      {
        key: 'cv_publication_consent',
        labelKey: 'pbl.lawyerRegistration.reviewSubmit.labels.publicationConsent',
        defaultLabel: 'CV publication consent',
        format: 'boolean',
      },
      {
        key: 'cv_full_name_en',
        labelKey: 'pbl.backlogEnhancements.cv.fields.cvNameEn',
        defaultLabel: 'CV name (English)',
      },
      {
        key: 'cv_full_name_zh',
        labelKey: 'pbl.backlogEnhancements.cv.fields.cvNameZh',
        defaultLabel: 'CV name (Chinese)',
      },
      {
        key: 'cv_law_firm_en',
        labelKey: 'pbl.backlogEnhancements.cv.fields.cvFirmEn',
        defaultLabel: 'CV law firm (English)',
      },
      {
        key: 'cv_law_firm_zh',
        labelKey: 'pbl.backlogEnhancements.cv.fields.cvFirmZh',
        defaultLabel: 'CV law firm (Chinese)',
      },
      {
        key: 'cv_position_en',
        labelKey: 'pbl.backlogEnhancements.cv.fields.cvPositionEn',
        defaultLabel: 'CV position (English)',
      },
      {
        key: 'cv_position_zh',
        labelKey: 'pbl.backlogEnhancements.cv.fields.cvPositionZh',
        defaultLabel: 'CV position (Chinese)',
      },
      {
        key: 'cv_admission_year_en',
        labelKey: 'pbl.lawyerRegistration.profileCv.fields.admissionYear',
        defaultLabel: 'CV admission year (English)',
      },
      {
        key: 'cv_admission_year_zh',
        labelKey: 'pbl.lawyerRegistration.profileCv.fields.admissionYearZh',
        defaultLabel: 'CV admission year (Chinese)',
      },
      {
        key: 'cv_other_qualifications_en',
        labelKey: 'pbl.backlogEnhancements.cv.fields.otherQualificationsEn',
        defaultLabel: 'Other qualifications (English)',
      },
      {
        key: 'cv_other_qualifications_zh',
        labelKey: 'pbl.backlogEnhancements.cv.fields.otherQualificationsZh',
        defaultLabel: 'Other qualifications (Chinese)',
      },
      {
        key: 'cv_languages_en',
        labelKey: 'pbl.backlogEnhancements.cv.fields.languagesEn',
        defaultLabel: 'Languages (English)',
      },
      {
        key: 'cv_languages_zh',
        labelKey: 'pbl.backlogEnhancements.cv.fields.languagesZh',
        defaultLabel: 'Languages (Chinese)',
      },
      {
        key: 'cv_primary_practice_areas_en',
        labelKey: 'pbl.backlogEnhancements.cv.fields.practiceAreasEn',
        defaultLabel: 'Practice areas (English)',
      },
      {
        key: 'cv_primary_practice_areas_zh',
        labelKey: 'pbl.backlogEnhancements.cv.fields.practiceAreasZh',
        defaultLabel: 'Practice areas (Chinese)',
      },
      {
        key: 'cv_professional_experience_en',
        labelKey: 'pbl.backlogEnhancements.cv.fields.professionalExperienceEn',
        defaultLabel: 'Professional experience (English)',
      },
      {
        key: 'cv_professional_experience_zh',
        labelKey: 'pbl.backlogEnhancements.cv.fields.professionalExperienceZh',
        defaultLabel: 'Professional experience (Chinese)',
      },
      {
        key: 'cv_pro_bono_experience_en',
        labelKey: 'pbl.backlogEnhancements.cv.fields.proBonoExperienceEn',
        defaultLabel: 'Pro bono experience (English)',
      },
      {
        key: 'cv_pro_bono_experience_zh',
        labelKey: 'pbl.backlogEnhancements.cv.fields.proBonoExperienceZh',
        defaultLabel: 'Pro bono experience (Chinese)',
      },
    ],
  },
];

const excludedFields = new Set([
  'approved_at',
  'created_at',
  'given_name_en',
  'given_name_zh',
  'id',
  'inactive_at',
  'reference_id',
  'status',
  'submitted_at',
  'surname_en',
  'surname_zh',
  'updated_at',
  'user_id',
  'vetting_round',
  'vetting_status',
]);

const hasOwn = (record: Record<string, unknown>, key: string) => Object.prototype.hasOwnProperty.call(record, key);

const normalizeBooleanLike = (value: unknown) => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : value;
  if (normalized === true || normalized === 1 || normalized === '1' || normalized === 'true' || normalized === 'yes') {
    return true;
  }
  if (normalized === false || normalized === 0 || normalized === '0' || normalized === 'false' || normalized === 'no') {
    return false;
  }
  return value;
};

const sortedUnique = (values: string[]) => Array.from(new Set(values)).sort();

const normalizeChoiceText = (
  value: unknown,
  normalize: (source: unknown) => string[],
  inferOtherText?: (source: unknown) => string
) => {
  const customText = inferOtherText?.(value);
  return sortedUnique([
    ...normalize(value),
    ...(customText
      ? customText
          .split(',')
          .map((item) => item.trim().toLowerCase())
          .filter(Boolean)
      : []),
  ]);
};

const normalizeForComparison = (field: ReviewFieldDefinition, value: unknown) => {
  if (value === undefined || value === null || value === '') return null;

  if (field.format === 'boolean' || field.key === 'cv_publication_consent') {
    return normalizeBooleanLike(value);
  }

  if (field.key === 'language_proficiency') return sortedUnique(normalizePblLanguageValues(value));
  if (field.key === 'other_professional_qualifications') {
    return sortedUnique(normalizePblProfessionalQualificationValues(value));
  }
  if (field.key === 'service_areas') return sortedUnique(normalizePblServiceAreaValues(value));
  if (field.key === 'position_in_law_firm') return normalizePblLawFirmPositionValue(value);
  if (field.key === 'cv_languages_en' || field.key === 'cv_languages_zh') {
    return normalizeChoiceText(value, normalizePblLanguageValues, inferPblLanguageOtherText);
  }
  if (field.key === 'cv_other_qualifications_en' || field.key === 'cv_other_qualifications_zh') {
    return normalizeChoiceText(
      value,
      normalizePblProfessionalQualificationValues,
      inferPblProfessionalQualificationOtherText
    );
  }
  if (field.key === 'cv_primary_practice_areas_en' || field.key === 'cv_primary_practice_areas_zh') {
    return sortedUnique(normalizePblServiceAreaValues(value));
  }
  if (field.key === 'cv_position_en' || field.key === 'cv_position_zh') {
    return normalizePblLawFirmPositionValue(value) || String(value).trim().toLowerCase();
  }
  if (Array.isArray(value)) return sortedUnique(value.map((item) => String(item).trim()));
  if (typeof value === 'string') return value.trim().replace(/\r\n/g, '\n');
  return value;
};

const valuesDiffer = (field: ReviewFieldDefinition, publishedValue: unknown, proposedValue: unknown) =>
  JSON.stringify(normalizeForComparison(field, publishedValue)) !==
  JSON.stringify(normalizeForComparison(field, proposedValue));

const humanizeFieldName = (field: string) =>
  field
    .replace(/^cv_/, '')
    .replace(/_(en|zh)$/, ' ($1)')
    .replaceAll('_', ' ')
    .replace(/^./, (character) => character.toUpperCase());

const getApiErrorMessage = (error: unknown) => {
  const apiError = error as { response?: { data?: { data?: unknown; message?: unknown } } };
  const message = apiError.response?.data?.message;
  const data = apiError.response?.data?.data;

  if (typeof message === 'string' && message) return message;
  if (typeof data === 'string' && data) return data;
  return '';
};

const PblVettingCvRevisionDetail = () => {
  const { revisionId } = useParams();
  const navigate = useNavigate();
  // @ts-ignore -- the project's generated i18next resource type exceeds CRA's compiler recursion limit in this view.
  const { t: translate } = useTranslation();
  const t = translate as unknown as ReviewTranslator;
  const [revision, setRevision] = useState<PblLawyerCvRevision | null>(null);
  const [published, setPublished] = useState<Partial<PblLawyerProfile>>({});
  const [publishedAuthorisation, setPublishedAuthorisation] = useState<PblCvAuthorisation>({});
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const tr = (key: string, defaultValue: string) => t(key as any, { defaultValue }) as string;
  const publishedRecord = published as Record<string, unknown>;
  const proposedRecord = (revision?.payload || {}) as Record<string, unknown>;

  useEffect(() => {
    let active = true;
    const parsedRevisionId = Number(revisionId);

    if (!Number.isFinite(parsedRevisionId)) {
      errorHandler(tr('pbl.backlogEnhancements.cvReview.loadFailed', 'Unable to load CV revision.'));
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    getPblLawyerCvRevisionForReview(parsedRevisionId)
      .then((response) => {
        if (!active) return;
        setRevision(response.data.data.revision);
        setPublished(response.data.data.published_payload || {});
        setPublishedAuthorisation(response.data.data.published_authorisation || {});
      })
      .catch((error: unknown) => {
        if (!active) return;
        errorHandler(
          getApiErrorMessage(error) || tr('pbl.backlogEnhancements.cvReview.loadFailed', 'Unable to load CV revision.')
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [revisionId]);

  const visibleSections = useMemo(
    () =>
      reviewSections
        .map((section) => ({
          ...section,
          fields: section.fields.filter(
            (field) => hasOwn(publishedRecord, field.key) || hasOwn(proposedRecord, field.key)
          ),
        }))
        .filter((section) => section.fields.length > 0),
    [publishedRecord, proposedRecord]
  );

  const additionalFields = useMemo(() => {
    const knownFields = new Set(reviewSections.flatMap((section) => section.fields.map((field) => field.key)));

    return Array.from(new Set([...Object.keys(publishedRecord), ...Object.keys(proposedRecord)]))
      .filter((field) => !knownFields.has(field) && !excludedFields.has(field))
      .sort()
      .map<ReviewFieldDefinition>((field) => ({
        key: field,
        labelKey: '',
        defaultLabel: humanizeFieldName(field),
      }));
  }, [publishedRecord, proposedRecord]);

  const displayValue = (value: unknown, format: FieldFormat = 'raw') => {
    if (value === undefined || value === null || value === '') return '-';
    if (format === 'boolean' || typeof value === 'boolean') {
      return normalizeBooleanLike(value) === true ? tr('pbl.common.yes', 'Yes') : tr('pbl.common.no', 'No');
    }
    if (format === 'list' || Array.isArray(value)) {
      return translatePblDisplayList(t, value as string | string[]);
    }
    if (format === 'value' && typeof value === 'string') return translatePblDisplayValue(t, value);
    return String(value);
  };

  const fieldLabel = (field: ReviewFieldDefinition) =>
    field.labelKey ? tr(field.labelKey, field.defaultLabel) : field.defaultLabel;

  const decide = async (decision: ReviewDecision) => {
    const trimmedReason = reason.trim();
    if (decision !== 'approve' && !trimmedReason) {
      setReasonError(
        tr(
          'pbl.backlogEnhancements.cvReview.reasonRequired',
          'Please enter a reason before returning or rejecting this revision.'
        )
      );
      return;
    }

    setReasonError('');
    setSubmitting(true);
    try {
      await reviewPblLawyerCvRevision(Number(revisionId), {
        decision,
        ...(trimmedReason ? { reason: trimmedReason } : {}),
      });
      successHandler(t(`pbl.backlogEnhancements.cvReview.messages.${decision}`));
      navigate(PATH.pbl.vettingCvRevisions);
    } catch (error: unknown) {
      errorHandler(
        getApiErrorMessage(error) || tr('pbl.backlogEnhancements.cvReview.reviewFailed', 'Unable to review CV.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: ReviewFieldDefinition) => {
    const publishedValue = publishedRecord[field.key];
    const proposedValue = proposedRecord[field.key];
    const changed = valuesDiffer(field, publishedValue, proposedValue);

    return (
      <FieldRow key={field.key} $changed={changed} data-review-field={field.key}>
        <FieldLabel>
          {fieldLabel(field)}
          {changed && (
            <>
              <br />
              <ChangedBadge>{tr('pbl.backlogEnhancements.cvReview.changed', 'Changed')}</ChangedBadge>
            </>
          )}
        </FieldLabel>
        <VersionValue $changed={false}>
          <MobileVersionLabel>{tr('pbl.backlogEnhancements.cvReview.published', 'Published')}</MobileVersionLabel>
          {displayValue(publishedValue, field.format)}
        </VersionValue>
        <VersionValue $changed={changed} $proposed>
          <MobileVersionLabel>{tr('pbl.backlogEnhancements.cvReview.proposed', 'Proposed')}</MobileVersionLabel>
          {displayValue(proposedValue, field.format)}
        </VersionValue>
      </FieldRow>
    );
  };

  const lawyerName =
    revision?.lawyer_profile?.full_name_en ||
    String(proposedRecord.full_name_en || publishedRecord.full_name_en || '') ||
    tr('pbl.backlogEnhancements.cvReview.lawyerFallback', `Lawyer #${revision?.pbl_lawyer_profile_id || '-'}`);
  const statusKey = revision?.status === 'resubmit_required' ? 'returned' : revision?.status;
  const statusLabel = statusKey
    ? tr(`pbl.backlogEnhancements.cv.status.${statusKey}`, humanizeFieldName(statusKey))
    : '-';
  const hasReviewFields = visibleSections.length > 0 || additionalFields.length > 0;

  return (
    <PageContainer title={tr('pbl.backlogEnhancements.cvReview.detailTitle', 'Review lawyer CV revision')}>
      <LoadingPage isLoading={loading || submitting} />
      {!loading && revision && (
        <ReviewPanel>
          <RevisionSummary>
            <LawyerName>{lawyerName}</LawyerName>
            <RevisionMeta>
              <span>
                {tr('pbl.backlogEnhancements.cvReview.revisionNumber', 'Revision {{number}}').replace(
                  '{{number}}',
                  String(revision.revision_no)
                )}
              </span>
              <span>{statusLabel}</span>
              {revision.submitted_at && (
                <span>
                  {tr('pbl.backlogEnhancements.cvReview.submittedAt', 'Submitted')}:{' '}
                  {formatPblDisplayDate(revision.submitted_at)}
                </span>
              )}
            </RevisionMeta>
          </RevisionSummary>

          <ReviewIntro>
            {tr(
              'pbl.backlogEnhancements.cvReview.intro',
              'Compare the currently published CV with the proposed revision. Changed values are highlighted.'
            )}
          </ReviewIntro>

          {hasReviewFields ? (
            <>
              {visibleSections.map((section) => (
                <ReviewSection key={section.key}>
                  <SectionTitle>{tr(section.titleKey, section.defaultTitle)}</SectionTitle>
                  <ComparisonHeader aria-hidden="true">
                    <span />
                    <span>{tr('pbl.backlogEnhancements.cvReview.published', 'Published')}</span>
                    <span>{tr('pbl.backlogEnhancements.cvReview.proposed', 'Proposed')}</span>
                  </ComparisonHeader>
                  {section.fields.map(renderField)}
                </ReviewSection>
              ))}

              {additionalFields.length > 0 && (
                <ReviewSection>
                  <SectionTitle>
                    {tr('pbl.backlogEnhancements.cvReview.additionalInformation', 'Additional information')}
                  </SectionTitle>
                  <ComparisonHeader aria-hidden="true">
                    <span />
                    <span>{tr('pbl.backlogEnhancements.cvReview.published', 'Published')}</span>
                    <span>{tr('pbl.backlogEnhancements.cvReview.proposed', 'Proposed')}</span>
                  </ComparisonHeader>
                  {additionalFields.map(renderField)}
                </ReviewSection>
              )}
            </>
          ) : (
            <EmptyState>
              {tr('pbl.backlogEnhancements.cvReview.noFields', 'No CV fields were provided for review.')}
            </EmptyState>
          )}

          <ReviewSection>
            <SectionTitle>
              {tr('pbl.lawyerRegistration.steps.authorisation', 'Authorisation from law firm')}
            </SectionTitle>
            {[
              {
                label: tr('pbl.backlogEnhancements.cvReview.published', 'Published'),
                data: publishedAuthorisation,
                id: undefined,
              },
              {
                label: tr('pbl.backlogEnhancements.cvReview.proposed', 'Proposed'),
                data: revision.authorisation,
                id: revision.id,
              },
            ].map(({ label, data, id }) => (
              <div key={label}>
                <h3>{label}</h3>
                {data?.firm_authorisation_file_id ? (
                  <CvAuthorisationDownload
                    profileId={revision.pbl_lawyer_profile_id}
                    revisionId={id}
                    fileName={data.file_name}
                  />
                ) : (
                  <p>-</p>
                )}
                {authorisationConfirmations.map(([field, key]) => (
                  <p key={field}>
                    {tr(`pbl.lawyerRegistration.authorisation.${key}`, key)}: {displayValue(data?.[field], 'boolean')}
                  </p>
                ))}
              </div>
            ))}
          </ReviewSection>

          <DecisionSection>
            <ReasonLabel htmlFor="cv-review-reason">
              {tr('pbl.backlogEnhancements.cvReview.reason', 'Reason (required for return or reject)')}
            </ReasonLabel>
            <ReasonHelp>
              {tr(
                'pbl.backlogEnhancements.cvReview.reasonHelp',
                'A reason is optional for approval and required when returning or rejecting a revision.'
              )}
            </ReasonHelp>
            <ReasonTextarea
              id="cv-review-reason"
              value={reason}
              $invalid={Boolean(reasonError)}
              aria-invalid={Boolean(reasonError)}
              aria-describedby={reasonError ? 'cv-review-reason-error' : undefined}
              placeholder={tr(
                'pbl.backlogEnhancements.cvReview.reasonPlaceholder',
                'Enter review comments for the lawyer'
              )}
              onChange={(event) => {
                setReason(event.target.value);
                if (reasonError) setReasonError('');
              }}
            />
            {reasonError && (
              <FieldError id="cv-review-reason-error" role="alert">
                {reasonError}
              </FieldError>
            )}
            <ActionRow>
              <Button
                type="button"
                size="md"
                variant="primary"
                rounded={false}
                disabled={submitting}
                onClick={() => decide('approve')}
              >
                {tr('pbl.backlogEnhancements.cvReview.approve', 'Approve')}
              </Button>
              <Button
                type="button"
                size="md"
                variant="primary-invert"
                rounded={false}
                disabled={submitting}
                onClick={() => decide('return')}
              >
                {tr('pbl.backlogEnhancements.cvReview.return', 'Return')}
              </Button>
              <Button
                type="button"
                size="md"
                variant="danger-invert"
                rounded={false}
                disabled={submitting}
                onClick={() => decide('reject')}
              >
                {tr('pbl.backlogEnhancements.cvReview.reject', 'Reject')}
              </Button>
            </ActionRow>
          </DecisionSection>
        </ReviewPanel>
      )}
    </PageContainer>
  );
};

export default PblVettingCvRevisionDetail;
